import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ProtectedRoute = ({ 
    children, 
    requiredRoles = [], 
    allowedRoles = [], 
    requiredPrivileges = [],
    allowedPrivileges = [],
    requireAll = false, // If true, user must have ALL specified roles/privileges
    requireBoth = false // If true, user must have BOTH roles AND privileges
}) => {
    const { 
        isAuthenticated, 
        loading, 
        hasAnyRole, 
        hasAllRoles, 
        hasAnyPrivilege, 
        hasAllPrivileges,
        hasRoleOrPrivilege,
        hasRoleAndPrivilege
    } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                ...Loading
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/users/login" state={{ from: location }} replace />;
    }

    // Check permissions based on configuration
    let hasPermission = true;

    if (requireBoth && (requiredRoles.length > 0 || requiredPrivileges.length > 0)) {
        // User must have both roles AND privileges
        hasPermission = hasRoleAndPrivilege(requiredRoles, requiredPrivileges);
    } else if (requiredRoles.length > 0 || requiredPrivileges.length > 0) {
        // User must have required roles OR privileges
        const roleCheck = requiredRoles.length > 0 ? 
            (requireAll ? hasAllRoles(requiredRoles) : hasAnyRole(requiredRoles)) : true;
        const privilegeCheck = requiredPrivileges.length > 0 ? 
            (requireAll ? hasAllPrivileges(requiredPrivileges) : hasAnyPrivilege(requiredPrivileges)) : true;
        
        hasPermission = roleCheck && privilegeCheck;
    } else if (allowedRoles.length > 0 || allowedPrivileges.length > 0) {
        // User can have any of the allowed roles OR privileges
        hasPermission = hasRoleOrPrivilege(allowedRoles, allowedPrivileges);
    }

    if (!hasPermission) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
                    <p className="text-gray-600">You don't have permission to access this page.</p>
                </div>
            </div>
        );
    }

    return children;
};

export default ProtectedRoute;
